<script setup lang="ts">
    import { computed, ref } from 'vue';
    import type { CaseNotification } from '@/api/caseSettings';
    import { DateTime } from 'luxon';
    import { taggedLogger } from '@/util';
    import { timeElapsed } from '@/util/dateTime';

    const props = defineProps<{
        notification?: CaseNotification;
        warning: boolean;
    }>();

    const log = taggedLogger('complete-toastie');

    const hideToastie = ref(false);

    const message = computed(() =>
        props.warning?
            'Processing complete, but no plan was generated. Review the case on the 3D page and generate a PDF plan.' :
            'Your case is finished and ready for review.',
    );


    const createdOn = computed(() => {
        if (!props.notification?.timestamp) return null;

        const timestamp = props.notification.timestamp;
        const when = DateTime.fromISO(timestamp);
        if (when) {
            return when.toRelativeCalendar({ unit: timeElapsed(when) });
        } else {
            log.warn('Failed to parse timestamp from notification message');
        }
        return null;
    });

    const onClose = () => {
        hideToastie.value = true;
    };
</script>

<template>
    <v-card width="389" class="mb-5" :class="hideToastie ? 'd-none' : ''">
        <v-layout>
            <v-icon
                class="ml-5 mt-3 notification-icon"
                size="25"
                color="complete"
                style="opacity: 1"
                icon="f:complete-tooltip"
            />
            <div class="ml-2 mt-1 pr-16">
                <v-card-title class="pl-2">Case Processing Complete</v-card-title>
                <v-card-text class="pl-2 pt-0 pb-2 justify-end">{{ message }}</v-card-text>
                <v-card-subtitle class="pl-2 pb-5 justify-end">
                    {{ createdOn }}
                </v-card-subtitle>
            </div>
            <div>
                <v-card-actions class="mr-1 position-absolute right-0 bottom-0">
                    <app-button-secondary class="got-it" data-testid="toastie-got-it" @click="onClose">
                        Got it
                    </app-button-secondary>
                </v-card-actions>
            </div>
        </v-layout>
    </v-card>
</template>

<style scoped lang="scss">
    .got-it {
        width: 60px !important;
        height: 30px !important;
    }

    :deep(.v-card-title) {
        font-weight: 700 !important;
        font-size: 14px !important;
    }

    :deep(.v-card-text) {
        font-weight: 400 !important;
        font-size: 14px;
    }
</style>
